import { readonly, ref } from 'vue'

/**
 * Whether the barcode gun is there (decision 0010, 2026-09-03).
 *
 * The page cannot see USB; the bridge can, and reports every change and every
 * answer to a status request. This holds the last report so the status strip
 * can say "No barcode gun" — the one thing that should be said before any
 * verdict, because nothing can be scanned until it is fixed.
 *
 * `unknown` is the honest start, and what a desktop stays at: no bridge, no
 * report, no error — the strip says "no gun on this device". Enterprise
 * Browser moves to `asking` when it asks the bridge, and marks a silence as a
 * missing gun only after asking and hearing nothing.
 */
export type GunPresence = 'unknown' | 'asking' | 'connected' | 'disconnected'

export type GunStatus = { presence: GunPresence, detail: string, at: number }

const status = ref<GunStatus>({ presence: 'unknown', detail: '', at: 0 })

export function reportGunStatus(report: { connected: boolean, detail: string }): void {
  status.value = {
    presence: report.connected ? 'connected' : 'disconnected',
    detail: report.detail,
    at: Date.now(),
  }
}

/** The bridge has been asked; a report is expected. Leaves a known answer alone. */
export function markGunAsking(): void {
  if (status.value.presence !== 'unknown') return
  status.value = { presence: 'asking', detail: '', at: Date.now() }
}

/**
 * The bridge did not answer. Counts as a missing gun only while nothing has
 * been heard at all — a report already in hand, either way, is not overruled
 * by a lost request.
 */
export function markGunUnanswered(detail: string): void {
  if (status.value.presence === 'connected' || status.value.presence === 'disconnected') return
  status.value = { presence: 'disconnected', detail, at: Date.now() }
}

/** For tests. */
export function resetGunStatus(): void {
  status.value = { presence: 'unknown', detail: '', at: 0 }
}

export function useGunStatus() {
  return readonly(status)
}
